import React from 'react';
import { Badge, Card } from 'react-bootstrap';
import PropTypes from 'prop-types';
import StatusPill from '@/components/common/StatusPill';
import { badgeTextFor } from '@/shared/utils/badgeText';
import { formatAnswerDay } from '@/shared/utils/deadline';
import {
    awardedCountText,
    decisionOutcome,
    programName,
} from '@/shared/utils/applicationStatus';
import './DecisionHistory.css';

const PROGRAM_BADGE = {
    si: { bg: 'success', label: 'SI' },
    ta: { bg: 'primary', label: 'TA' },
    // No Bootstrap fill: a bg-* utility is !important and would hide the
    // program colour that badge-la sets
    la: { bg: '', label: 'LA', className: 'badge-la' },
};

const isNumber = (value) => typeof value === 'number';

function CompactRecord({ decision }) {
    const program = PROGRAM_BADGE[decision.programType];
    return (
        <li className="d-flex align-items-center gap-2 mb-1">
            <Badge
                bg={program ? program.bg : 'secondary'}
                text={badgeTextFor(program ? program.bg : 'secondary')}
                className={program?.className}
            >
                {program?.label || String(decision.programType).toUpperCase()}
            </Badge>
            <StatusPill family="decision" decision={decision} />
        </li>
    );
}

CompactRecord.propTypes = { decision: PropTypes.object.isRequired };

function PartialDetails({ details }) {
    if (!details) return null;
    const { offeredHours, requestedHours, conditions } = details;
    return (
        <>
            {isNumber(offeredHours) && isNumber(requestedHours) && (
                <div>
                    Offered {offeredHours} of {requestedHours} hours requested
                </div>
            )}
            {conditions && (
                <div>
                    <span className="text-muted">Conditions: </span>
                    <span className="text-break">{conditions}</span>
                </div>
            )}
        </>
    );
}

PartialDetails.propTypes = { details: PropTypes.object };

function FullRecord({ decision, showMessage }) {
    const outcome = decisionOutcome(decision);
    const countText = awardedCountText(decision);
    const settled =
        decision.type === 'contingent' &&
        ['finalized', 'voided'].includes(decision.finalStatus) &&
        decision.finalizedAt;
    return (
        <li className="border-top pt-3 mt-3">
            <div className="d-flex flex-wrap align-items-center gap-2 mb-1">
                <strong>{programName(decision.programType)}</strong>
                <StatusPill family="decision" decision={decision} />
            </div>
            {countText && <div>{countText}</div>}
            {decision.type === 'partial' && (
                <PartialDetails details={decision.partialDetails} />
            )}
            <small className="text-muted d-block">
                Decided {formatAnswerDay(decision.createdAt)}
            </small>
            {settled && (
                <small className="text-muted d-block">
                    Settled {formatAnswerDay(decision.finalizedAt)}
                </small>
            )}
            {decision.decidedBy?.name && (
                <small className="text-muted d-block">
                    by {decision.decidedBy.name}
                </small>
            )}
            {showMessage && outcome.nextStep && (
                <p className="mb-0 mt-2">{outcome.nextStep}</p>
            )}
            {showMessage && decision.messageToFaculty && (
                <div className="mt-2">
                    <div className="fw-semibold small">
                        Message from the program
                    </div>
                    <p className="mb-0 text-break text-pre-line">
                        {decision.messageToFaculty}
                    </p>
                </div>
            )}
        </li>
    );
}

FullRecord.propTypes = {
    decision: PropTypes.object.isRequired,
    showMessage: PropTypes.bool.isRequired,
};

/**
 * The decisions the server released to this viewer, as it built them. It
 * renders what it is given and decides nothing about visibility: during a
 * hold the list is empty and nothing shows. A record carries no coordinator
 * notes, and none are read here. `showMessages` off leaves out each
 * decision's next step and message, for a page that shows them elsewhere.
 */
function DecisionHistory({
    decisions,
    compact = false,
    headingLevel = 2,
    showMessages = true,
}) {
    if (!Array.isArray(decisions) || decisions.length === 0) return null;

    if (compact) {
        return (
            <ul className="list-unstyled mb-0">
                {decisions.map((decision) => (
                    <CompactRecord key={decision._id} decision={decision} />
                ))}
            </ul>
        );
    }

    const Heading = `h${headingLevel}`;
    return (
        <Card className="border-0 shadow-sm mb-4">
            <Card.Body>
                <Heading className="h5 mb-0">Decision record</Heading>
                <ul className="list-unstyled mb-0">
                    {decisions.map((decision) => (
                        <FullRecord
                            key={decision._id}
                            decision={decision}
                            showMessage={showMessages}
                        />
                    ))}
                </ul>
            </Card.Body>
        </Card>
    );
}

DecisionHistory.propTypes = {
    decisions: PropTypes.arrayOf(PropTypes.object),
    compact: PropTypes.bool,
    headingLevel: PropTypes.number,
    showMessages: PropTypes.bool,
};

export default DecisionHistory;
